import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Editor } from '@tiptap/core'
import { centerViewOnPosition } from '../utils/center-view-on-position'

@Component({
  selector: 'app-find-replace-panel',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './find-replace-panel.component.html',
  styleUrl: './find-replace-panel.component.scss',
})
export class FindReplacePanelComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input({ required: true })
  editor!: Editor

  @Input()
  canUserEdit = false

  @Output()
  closed = new EventEmitter<void>()

  @Output()
  replaceRequested = new EventEmitter<void>()

  @Output()
  replaceAllRequested = new EventEmitter<void>()

  @ViewChild('searchInput')
  searchInputRef?: ElementRef<HTMLInputElement>

  private translateService = inject(TranslateService)
  private changeDetectorRef = inject(ChangeDetectorRef)

  searchTerm = ''
  replaceTerm = ''
  resultCount = 0
  currentIndex: number | null = null

  private handleTransaction = () => {
    this.syncFromStorage()
    this.changeDetectorRef.markForCheck()
  }

  ngOnInit() {
    this.searchTerm = this.editor.storage.findAndReplace.searchTerm
    this.replaceTerm = this.editor.storage.findAndReplace.replaceTerm
    this.syncFromStorage()
    this.editor.on('transaction', this.handleTransaction)
  }

  ngAfterViewInit() {
    this.focusSearchInput()
  }

  focusSearchInput() {
    this.searchInputRef?.nativeElement.focus()
    this.searchInputRef?.nativeElement.select()
  }

  ngOnDestroy() {
    this.editor.off('transaction', this.handleTransaction)
  }

  get resultCountLabel(): string {
    if (this.searchTerm.length === 0) {
      return ''
    }
    if (this.resultCount === 0) {
      return this.translateService.instant('EDITOR.FIND_REPLACE.NO_RESULTS')
    }
    const current = this.currentIndex !== null ? this.currentIndex + 1 : 0
    return this.translateService.instant('EDITOR.FIND_REPLACE.RESULT_COUNT', { current, total: this.resultCount })
  }

  onSearchTermChange(term: string) {
    this.editor.commands.setSearchTerm(term)
  }

  onReplaceTermChange(term: string) {
    this.editor.commands.setReplaceTerm(term)
  }

  onSearchInputEnter(event: Event) {
    event.preventDefault()
    if ((event as KeyboardEvent).shiftKey) {
      this.goToPrevious()
    } else {
      this.goToNext()
    }
  }

  goToNext() {
    this.editor.commands.goToNextResult()
  }

  goToPrevious() {
    this.editor.commands.goToPreviousResult()
  }

  replace() {
    this.replaceRequested.emit()
  }

  replaceAll() {
    this.replaceAllRequested.emit()
    // A tracked replace-all clears the underlying search (see find-replace-track-changes.ts), which the
    // listener above only picks up for resultCount/currentIndex — searchTerm needs the same refresh so the
    // input doesn't keep showing a term whose search was just cleared out from under it.
    this.searchTerm = this.editor.storage.findAndReplace.searchTerm
  }

  onClose() {
    this.editor.commands.clearSearch()
    this.closed.emit()
  }

  private syncFromStorage() {
    const storage = this.editor.storage.findAndReplace
    const previousIndex = this.currentIndex
    this.resultCount = storage.results.length
    this.currentIndex = storage.currentIndex
    if (this.currentIndex !== null && this.currentIndex !== previousIndex) {
      centerViewOnPosition(this.editor.view, storage.results[this.currentIndex].from, { onlyIfOutOfView: true })
    }
  }
}
